import { UseInfiniteQueryOptions } from 'react-query';
import { useInfiniteQuery } from 'app-react-query/hooks';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import {
  AnalyticsCompany,
  GetCompaniesQuery,
  GetCompaniesQueryVariables,
  useGetCompaniesQuery,
} from 'api';
import { InfiniteQuery } from 'types/app';
import { useNotify } from 'hooks';
import { MAX_ADVANCED_FILTER_QUERIES, PAGINATION_ROOT_MARGIN } from 'app-constants';
import { useTranslation } from 'react-i18next';

type Query = GetCompaniesQuery;
type Variables = GetCompaniesQueryVariables;
type Options = UseInfiniteQueryOptions<Query>;
type ReturnType = InfiniteQuery<AnalyticsCompany>;

const useGetCompanies = (variables: Variables, options?: Options): ReturnType => {
  const notify = useNotify();
  const { t } = useTranslation();
  const exceedsFilterLimit =
    variables?.advanced_filters?.users?.length >= MAX_ADVANCED_FILTER_QUERIES;

  if (exceedsFilterLimit) {
    variables.limit = 0; // return no results
  }

  const queryResult = useInfiniteQuery<Query, Variables>(
    useGetCompaniesQuery,
    ({ pageParam }: { pageParam: Variables }) => ({ ...variables, ...pageParam }),
    {
      initialData: {
        pages: [{ getCompanies: { total: 0, results: [] } }],
        pageParams: [],
      },
      onError: notify.queryError,
      onSettled: () => {
        if (exceedsFilterLimit) {
          notify.info({ message: t('insights:max_advanced_filter_query_error'), duration: 5000 });
        }
      },
      getNextPageParam: (lastPage, allPages) => {
        const { getCompanies } = lastPage;
        const noResults = !getCompanies.results.length;

        if (noResults) {
          return false;
        }

        return { offset: allPages.map(page => page.getCompanies.results).flat().length };
      },
      ...options,
    }
  );

  const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = queryResult;
  const results = data.pages.map(page => page.getCompanies.results).flat();
  const total = data.pages[0].getCompanies.total;

  const [sentinelRef] = useInfiniteScroll({
    hasNextPage,
    loading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
    rootMargin: PAGINATION_ROOT_MARGIN,
  });

  return {
    sentinelRef,
    results,
    count: total,
    isEmpty: !total && !isFetching,
    isExhausted: !hasNextPage,
    isInitialLoad: !results.length && isFetching,
  };
};

export default useGetCompanies;
